// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The 3-benchmark performance matrix for a range (P or R): the portfolio return at each
// risk level beside all THREE benchmarks (CBAM composite, AFI, ARC MPS) and the excess vs
// each. Always shows three benchmarks — never one in isolation (house rule). Every figure
// is a governed Cell rendered through CellValue (provenance on hover, sign-toned excesses).

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Cell, IR } from "@/types/ir";
import { CellValue } from "./CellValue";
import { EmptyState } from "@/components/research-hub";
import { type PeriodKey, periodLabel } from "./labels";

// `period` is REQUIRED. It used to default to "q2", which silently rendered a monthly-interim
// cycle's matrix against a window that cycle never computed — every cell a not_found sentinel
// over data that was present under "m1". Callers must derive it from the cycle via
// primaryPeriodKey(cycle.period_type) so the read side cannot drift from app.py:710.
export function BenchmarkMatrix({
  ir,
  range,
  period,
}: {
  ir: IR | undefined;
  range: "P" | "R";
  period: PeriodKey;
}) {
  const returns = ir?.data?.portfolio_returns ?? {};
  const benchmarks = ir?.data?.benchmarks ?? {};
  const excesses = ir?.data?.excesses ?? {};

  // levels 1..5 for the chosen range; skip any the fixture omits
  const levels = [1, 2, 3, 4, 5].filter((n) => returns[`${range}${n}`]);
  if (levels.length === 0) return <EmptyState title={`No ${range}-range returns in this cycle yet.`} />;

  const cell = (v: unknown): Cell | undefined => (v as Cell) ?? undefined;

  return (
    <div className="overflow-x-auto">
      <p className="font-body text-xs text-muted-foreground pb-2">
        Return window: {periodLabel(period)} ({period})
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Level</TableHead>
            <TableHead className="text-right">Portfolio</TableHead>
            <TableHead className="text-right">CBAM</TableHead>
            <TableHead className="text-right">AFI</TableHead>
            <TableHead className="text-right">ARC MPS</TableHead>
            <TableHead className="text-right">vs CBAM</TableHead>
            <TableHead className="text-right">vs AFI</TableHead>
            <TableHead className="text-right">vs ARC</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {levels.map((n) => {
            const pid = `${range}${n}`;
            const bm = benchmarks[n] ?? {};
            const ex = excesses[pid] ?? {};
            return (
              <TableRow key={pid}>
                <TableCell className="font-body font-medium">{pid}</TableCell>
                <TableCell className="text-right"><CellValue cell={cell(returns[pid]?.[period])} /></TableCell>
                <TableCell className="text-right"><CellValue cell={cell(bm.cbam?.[period])} /></TableCell>
                <TableCell className="text-right"><CellValue cell={cell(bm.afi?.[period])} /></TableCell>
                <TableCell className="text-right"><CellValue cell={cell(bm.arc?.[period])} /></TableCell>
                <TableCell className="text-right"><CellValue cell={cell(ex.vs_cbam?.[period])} signed /></TableCell>
                <TableCell className="text-right"><CellValue cell={cell(ex.vs_afi?.[period])} signed /></TableCell>
                <TableCell className="text-right"><CellValue cell={cell(ex.vs_arc?.[period])} signed /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
